import { KeyboardEvent } from 'react';
import { IconPencil, IconTruck } from '@tabler/icons';
import clsx from 'clsx';

import { Badge } from '#root/components/ui';
import { shippingConfigBadge } from '#root/models/Shipping';
import type { DealPreconfirm } from '#root/models/Shipping';
import styles from './shippingConfigBadge.module.scss';

interface ShippingConfigBadgeProps {
  preconfirm?: DealPreconfirm | null;
  // En tránsito: abre el drawer "Configurar envío"
  onConfigure?: () => void;
  // Ya en la oficina de envíos: abre el modal de Mis Envíos
  onManage?: () => void;
  className?: string;
}

/**
 * Badge "Envío Nacional" del acuerdo: muestra cómo va su envío y, según el estado, funciona como
 * botón (§7.2). El drawer y el modal los monta el padre, fuera del badge.
 */
const ShippingConfigBadge = (props: ShippingConfigBadgeProps) => {
  const { preconfirm, onConfigure, onManage, className } = props;
  const { text, action, editIcon } = shippingConfigBadge(preconfirm);
  const handleAction = action === 'configure' ? onConfigure : action === 'manage' ? onManage : undefined;
  // El nombre accesible incluye el texto visible
  const ariaLabel =
    action === 'configure' ? `${text}: configurar envío` : `${text}: ver cómo sigue tu envío`;

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // Espacio no debe desplazar la página
    event.preventDefault();
    handleAction();
  };

  return (
    <Badge
      variant="shipping"
      icon={<IconTruck size={16} />}
      className={clsx(styles.root, className)}
      title={text}
      onClick={handleAction}
      onKeyDown={handleAction ? handleKeyDown : undefined}
      tabIndex={handleAction ? 0 : undefined}
      aria-label={handleAction ? ariaLabel : undefined}>
      <span className={styles.text}>{text}</span>
      {editIcon && <IconPencil size={14} className={styles.editIcon} aria-hidden="true" />}
    </Badge>
  );
};

export default ShippingConfigBadge;
